import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./Content";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const ShortLongCalculator = () => {
  const content = article.find((item) => item.id === 2);
  const [mode, setMode] = useState("trader");
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [riskLevel, setRiskLevel] = useState("medium");
  const [notifications, setNotifications] = useState([]);
  const [positionType, setPositionType] = useState("Long");

  const [inputs, setInputs] = useState({
    entryPrice: "50.00",
    exitPrice: "65.00",
    quantity: "100",
    brokerage: "5.00",
    taxes: "15.00",
    leverage: "1",
    slippage: "2.00",
    financingCost: "0.00",
    holdingPeriod: "30",
    currencyRate: "1",
    dividend: "0.50",
    otherFees: "3.00",
    marginUsed: "100",
    stopLoss: "45.00",
    takeProfit: "70.00",
    riskAmount: "100",
    initialMargin: "5000",
    maintenanceMargin: "4000",
    fundingRate: "0.01"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateProfit();
    }
  }, [inputs, positionType, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculateProfit = () => {
    const {
      entryPrice, exitPrice, quantity, brokerage, taxes, leverage,
      slippage, financingCost, holdingPeriod, currencyRate, dividend, otherFees,
      marginUsed, stopLoss, takeProfit, fundingRate
    } = inputs;

    if (!entryPrice || !exitPrice || !quantity) {
      return;
    }

    const entry = parseFloat(entryPrice);
    const exit = parseFloat(exitPrice);
    const qty = parseFloat(quantity);
    const lev = parseFloat(leverage) || 1;
    const rate = parseFloat(currencyRate) || 1;
    const margin = parseFloat(marginUsed) || 0;

    // Calculate gross profit based on position type
    let grossProfit;
    if (positionType === "Long") {
      grossProfit = (exit - entry) * qty * lev;
    } else {
      // Short position calculation
      grossProfit = (entry - exit) * qty * lev;
      
      // Deduct financing costs for short positions
      const financing = parseFloat(financingCost || 0) + (parseFloat(fundingRate || 0) * margin * (parseFloat(holdingPeriod || 0) / 365));
      grossProfit -= financing;
    }

    // Calculate costs and net profit
    const totalCosts = parseFloat(brokerage || 0) + parseFloat(taxes || 0) + 
                      parseFloat(slippage || 0) + parseFloat(otherFees || 0);
    
    const netDividend = parseFloat(dividend || 0) * qty;
    const netProfit = (grossProfit - totalCosts + netDividend) * rate;

    // Calculate ROI based on margin used or total investment
    const totalInvestment = margin > 0 ? margin : (entry * qty * lev);
    const roi = totalInvestment > 0 ? (netProfit / totalInvestment) * 100 : 0;

    // Risk management calculations
    const stopLossPrice = parseFloat(stopLoss || 0);
    const takeProfitPrice = parseFloat(takeProfit || 0);
    
    let potentialLoss = 0;
    let potentialGain = 0;

    if (positionType === "Long") {
      potentialLoss = stopLossPrice ? ((stopLossPrice - entry) * qty * lev) : 0;
      potentialGain = takeProfitPrice ? ((takeProfitPrice - entry) * qty * lev) : 0;
    } else {
      potentialLoss = stopLossPrice ? ((entry - stopLossPrice) * qty * lev) : 0;
      potentialGain = takeProfitPrice ? ((entry - takeProfitPrice) * qty * lev) : 0;
    }

    const riskRewardRatio = potentialLoss !== 0 ? Math.abs(potentialGain / potentialLoss) : 0;

    // Margin calculations
    const initialMarginReq = parseFloat(inputs.initialMargin) || totalInvestment;
    const maintenanceMarginReq = parseFloat(inputs.maintenanceMargin) || (initialMarginReq * 0.8);
    const marginUtilization = (totalInvestment / initialMarginReq) * 100;

    const resultData = {
      grossProfit: grossProfit * rate,
      netProfit,
      roi,
      totalInvestment,
      totalCosts: totalCosts * rate,
      netDividend: netDividend * rate,
      positionType,
      leverageUsed: lev,
      breakEvenPrice: positionType === "Long" 
        ? (entry + (totalCosts / (qty * lev))).toFixed(2)
        : (entry - (totalCosts / (qty * lev))).toFixed(2),
      potentialLoss,
      potentialGain,
      riskRewardRatio: riskRewardRatio.toFixed(2),
      marginUtilization: marginUtilization.toFixed(2),
      liquidationPrice: calculateLiquidationPrice(),
      positionSize: totalInvestment
    };

    setResult(resultData);
  };

  const calculateLiquidationPrice = () => {
    const { entryPrice, quantity, leverage, initialMargin, maintenanceMargin } = inputs;
    const entry = parseFloat(entryPrice);
    const qty = parseFloat(quantity);
    const lev = parseFloat(leverage) || 1;
    const maintMargin = parseFloat(maintenanceMargin) || 0;

    if (maintMargin === 0) return "N/A";

    if (positionType === "Long") {
      const liquidation = entry - (maintMargin / (qty * lev));
      return Math.max(liquidation, 0).toFixed(2);
    } else {
      const liquidation = entry + (maintMargin / (qty * lev));
      return liquidation.toFixed(2);
    }
  };

  const calculateProjection = () => {
    const { initialMargin, holdingPeriod, leverage } = inputs;
    
    if (!initialMargin) return;

    const initial = parseFloat(initialMargin);
    const months = parseInt(holdingPeriod || 30) / 30; // Convert days to approximate months
    const lev = parseFloat(leverage || 1);

    const projectionData = [];
    let currentValue = initial;

    for (let i = 0; i <= Math.min(months, 24); i++) {
      if (i > 0) {
        // Simulate price movement with leverage effect
        const monthlyReturn = (Math.random() * 20 - 10) / 100; // -10% to +10% monthly
        currentValue = currentValue * (1 + (monthlyReturn * lev));
      }
      projectionData.push({
        month: i,
        value: Math.max(currentValue, 0),
        leverage: lev
      });
    }

    return projectionData;
  };

  const calculateRiskMetrics = () => {
    const { riskAmount, entryPrice, stopLoss, quantity, leverage } = inputs;
    
    if (!riskAmount || !entryPrice || !stopLoss) return null;

    const risk = parseFloat(riskAmount);
    const entry = parseFloat(entryPrice);
    const sl = parseFloat(stopLoss);
    const qty = parseFloat(quantity) || 100;
    const lev = parseFloat(leverage) || 1;
    
    const riskPerShare = positionType === "Long" ? (entry - sl) : (sl - entry);
    const positionSize = risk / riskPerShare;
    const totalInvestment = positionSize * entry * lev;

    return {
      riskPerShare: Math.abs(riskPerShare),
      positionSize: Math.floor(positionSize),
      totalInvestment,
      riskPercentage: (risk / totalInvestment * 100).toFixed(2),
      maxDrawdown: (riskPerShare / entry * 100).toFixed(2)
    };
  };

  const resetCalculator = () => {
    setInputs({
      entryPrice: "50.00",
      exitPrice: "65.00",
      quantity: "100",
      brokerage: "5.00",
      taxes: "15.00",
      leverage: "1",
      slippage: "2.00",
      financingCost: "0.00",
      holdingPeriod: "30",
      currencyRate: "1",
      dividend: "0.50",
      otherFees: "3.00",
      marginUsed: "100",
      stopLoss: "45.00",
      takeProfit: "70.00",
      riskAmount: "100",
      initialMargin: "5000",
      maintenanceMargin: "4000",
      fundingRate: "0.01"
    });
    setPositionType("Long");
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Position Type,${result.positionType}`,
      `Net Profit,${result.netProfit.toFixed(2)}`,
      `ROI,${result.roi.toFixed(2)}%`,
      `Total Investment,${result.totalInvestment.toFixed(2)}`,
      `Leverage,${result.leverageUsed}x`,
      `Risk/Reward Ratio,${result.riskRewardRatio}`,
      `Break-even Price,${result.breakEvenPrice}`,
      `Liquidation Price,${result.liquidationPrice}`,
      `Margin Utilization,${result.marginUtilization}%`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'short-long-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      positionType,
      inputs: { ...inputs },
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setPositionType(calc.positionType);
    setResult(calc);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { leverage: "1", stopLoss: "5", takeProfit: "10" },
      moderate: { leverage: "2", stopLoss: "8", takeProfit: "16" },
      aggressive: { leverage: "3", stopLoss: "12", takeProfit: "24" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} preset applied`, "info");
  };

  // Chart Data
  const profitChartData = result ? {
    labels: ["Gross Profit", "Fees & Costs", "Financing", "Dividends"],
    datasets: [{
      data: [
        Math.max(result.grossProfit, 0), 
        Math.max(result.totalCosts, 0),
        Math.max(parseFloat(inputs.financingCost || 0), 0),
        Math.max(result.netDividend, 0)
      ],
      backgroundColor: ["#28A745", "#FF6B6B", "#E53E3E", "#FFD93D"],
      borderWidth: 2,
      borderColor: darkMode ? "#1A202C" : "#FFFFFF",
      hoverOffset: 15
    }],
  } : null;

  const projectionData = calculateProjection();
  const projectionChartData = projectionData ? {
    labels: projectionData.map(d => `Month ${d.month}`),
    datasets: [
      {
        label: 'Portfolio Value',
        data: projectionData.map(d => d.value),
        borderColor: positionType === "Long" ? "#28A745" : "#E53E3E",
        backgroundColor: positionType === "Long" ? "rgba(40, 167, 69, 0.1)" : "rgba(229, 62, 62, 0.1)",
        fill: true,
        tension: 0.4,
        pointBackgroundColor: positionType === "Long" ? "#28A745" : "#E53E3E",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2
      }
    ]
  } : null;

  const riskMetrics = calculateRiskMetrics();

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Short/Long Position Calculator</h1>
        </div>
        <p>Advanced calculations for both long and short positions with leverage and risk management</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.positionType} Position</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.netProfit >= 0 ? 'positive' : 'negative'}`}>
                        ${calc.netProfit.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Position Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "projection" ? "active" : ""}`}
                onClick={() => setActiveTab("projection")}
              >
                <i className="fas fa-chart-line"></i>
                {isMobile ? 'Projection' : 'Price Projection'}
              </button>
              <button 
                className={`tab ${activeTab === "risk" ? "active" : ""}`}
                onClick={() => setActiveTab("risk")}
              >
                <i className="fas fa-shield-alt"></i>
                {isMobile ? 'Risk' : 'Risk Management'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Position Type Toggle */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${positionType === "Long" ? "active" : ""}`}
                onClick={() => setPositionType("Long")}
              >
                <i className="fas fa-arrow-up"></i>
                Long Position
              </button>
              <button 
                className={`mode-btn ${positionType === "Short" ? "active" : ""}`}
                onClick={() => setPositionType("Short")}
              >
                <i className="fas fa-arrow-down"></i>
                Short Position
              </button>
            </div>
            <div className="mode-description">
              {positionType === "Long" 
                ? "Buy low, sell high - Profit when price increases"
                : "Sell high, buy low - Profit when price decreases"
              }
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Risk Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-chart-bar"></i>
                    Position Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Entry Price</label>
                      <input 
                        type="number" 
                        name="entryPrice" 
                        value={inputs.entryPrice} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-layer-group"></i> Quantity</label>
                      <input 
                        type="number" 
                        name="quantity" 
                        value={inputs.quantity} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Exit Price</label>
                      <input 
                        type="number" 
                        name="exitPrice" 
                        value={inputs.exitPrice} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Brokerage Fees</label>
                      <input 
                        type="number" 
                        name="brokerage" 
                        value={inputs.brokerage} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Taxes & Charges</label>
                      <input 
                        type="number" 
                        name="taxes" 
                        value={inputs.taxes} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-rocket"></i> Leverage</label>
                      <select name="leverage" value={inputs.leverage} onChange={handleChange}>
                        <option value="1">1x (No Leverage)</option>
                        <option value="2">2x</option>
                        <option value="3">3x</option>
                        <option value="5">5x</option>
                        <option value="10">10x</option>
                      </select>
                    </div>
                  </div>

                  {/* Advanced Inputs */}
                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-repeat"></i> Slippage/Spread</label>
                      <input 
                        type="number" 
                        name="slippage" 
                        value={inputs.slippage} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currencyRate" 
                        value={inputs.currencyRate} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill-wave"></i> Dividend/Rebate</label>
                      <input 
                        type="number" 
                        name="dividend" 
                        value={inputs.dividend} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Other Fees</label>
                      <input 
                        type="number" 
                        name="otherFees" 
                        value={inputs.otherFees} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-balance-scale"></i> Margin Used</label>
                      <input 
                        type="number" 
                        name="marginUsed" 
                        value={inputs.marginUsed} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {positionType === "Short" && (
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-calendar"></i> Holding Period (Days)</label>
                        <input 
                          type="number" 
                          name="holdingPeriod" 
                          value={inputs.holdingPeriod} 
                          onChange={handleChange}
                          placeholder="30"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-dollar-sign"></i> Financing Cost</label>
                        <input 
                          type="number" 
                          name="financingCost" 
                          value={inputs.financingCost} 
                          onChange={handleChange}
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-percentage"></i> Funding Rate (%)</label>
                        <input 
                          type="number" 
                          name="fundingRate" 
                          value={inputs.fundingRate} 
                          onChange={handleChange}
                          placeholder="0.01"
                          step="0.001"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Projection Tab */}
          {activeTab === "projection" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-project-diagram"></i>
                  Price Projection
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-seedling"></i> Initial Margin</label>
                    <input 
                      type="number" 
                      name="initialMargin" 
                      value={inputs.initialMargin} 
                      onChange={handleChange}
                      placeholder="5000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-calendar"></i> Holding Period (Days)</label>
                    <input 
                      type="number" 
                      name="holdingPeriod" 
                      value={inputs.holdingPeriod} 
                      onChange={handleChange}
                      placeholder="30"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-rocket"></i> Leverage</label>
                    <select name="leverage" value={inputs.leverage} onChange={handleChange}>
                      <option value="1">1x</option>
                      <option value="2">2x</option>
                      <option value="3">3x</option>
                      <option value="5">5x</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Risk Management Tab */}
          {activeTab === "risk" && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-shield-alt"></i>
                  Risk Management
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-exclamation-triangle"></i> Risk Amount ($)</label>
                    <input 
                      type="number" 
                      name="riskAmount" 
                      value={inputs.riskAmount} 
                      onChange={handleChange}
                      placeholder="100"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-arrow-down"></i> Stop Loss Price</label>
                    <input 
                      type="number" 
                      name="stopLoss" 
                      value={inputs.stopLoss} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-arrow-up"></i> Take Profit Price</label>
                    <input 
                      type="number" 
                      name="takeProfit" 
                      value={inputs.takeProfit} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-piggy-bank"></i> Initial Margin</label>
                    <input 
                      type="number" 
                      name="initialMargin" 
                      value={inputs.initialMargin} 
                      onChange={handleChange}
                      placeholder="5000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-shield"></i> Maintenance Margin</label>
                    <input 
                      type="number" 
                      name="maintenanceMargin" 
                      value={inputs.maintenanceMargin} 
                      onChange={handleChange}
                      placeholder="4000"
                    />
                  </div>
                </div>

                {riskMetrics && (
                  <div className="risk-results">
                    <h4>Position Sizing</h4>
                    <div className="risk-metrics">
                      <div className="risk-metric">
                        <span className="metric-label">Shares to Trade:</span>
                        <span className="metric-value">{riskMetrics.positionSize}</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Total Investment:</span>
                        <span className="metric-value">${riskMetrics.totalInvestment.toFixed(2)}</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Risk Percentage:</span>
                        <span className="metric-value">{riskMetrics.riskPercentage}%</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Max Drawdown:</span>
                        <span className="metric-value">{riskMetrics.maxDrawdown}%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-type ${calc.positionType.toLowerCase()}`}>
                          {calc.positionType}
                        </span>
                        <span className={`history-profit ${calc.netProfit >= 0 ? 'positive' : 'negative'}`}>
                          <i className={`fas ${calc.netProfit >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                          ${calc.netProfit.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-chart-line"></i> ROI: {calc.roi.toFixed(2)}%</span>
                        <span><i className="fas fa-rocket"></i> Leverage: {calc.leverageUsed}x</span>
                        <span><i className="fas fa-balance-scale"></i> Break-even: ${calc.breakEvenPrice}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "projection" || activeTab === "risk") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className={`fas ${positionType === "Long" ? "fa-arrow-up" : "fa-arrow-down"}`}></i>
                    {positionType} Position Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className={`result-value ${result.netProfit >= 0 ? 'positive' : 'negative'}`}>
                          ${result.netProfit.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Net Profit/Loss
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.roi.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          ROI
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.leverageUsed}x
                        </div>
                        <div className="result-label">
                          <i className="fas fa-rocket"></i>
                          Leverage
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.riskRewardRatio}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-balance-scale"></i>
                          Risk/Reward
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-piggy-bank"></i> Total Investment:</span>
                        <span>${result.totalInvestment.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Gross Profit:</span>
                        <span>${result.grossProfit.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-receipt"></i> Total Costs:</span>
                        <span>${result.totalCosts.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Net Dividend/Rebate:</span>
                        <span>${result.netDividend.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-arrow-down"></i> Break-even Price:</span>
                        <span>${result.breakEvenPrice}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-exclamation-triangle"></i> Liquidation Price:</span>
                        <span>${result.liquidationPrice}</span>
                      </div>
                    </div>

                    {/* Risk Metrics */}
                    <div className="risk-summary">
                      <h4>Risk Assessment</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value negative">
                            ${result.potentialLoss.toFixed(2)}
                          </div>
                          <div className="risk-label">Potential Loss</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value positive">
                            ${result.potentialGain.toFixed(2)}
                          </div>
                          <div className="risk-label">Potential Gain</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            {result.marginUtilization}%
                          </div>
                          <div className="risk-label">Margin Used</div>
                        </div>
                      </div>
                    </div>

                    {/* Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Profit Distribution
                      </h3>
                      <Doughnut 
                        data={profitChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your position details to see calculations</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "projection" && projectionData && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-line"></i>
                  {positionType} Position Projection
                </h3>
                <div className="chart-container">
                  <Line 
                    data={projectionChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: false
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value">
                      ${projectionData[projectionData.length - 1]?.value.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-flag-checkered"></i>
                      Projected Value
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {inputs.leverage}x
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-rocket"></i>
                      Leverage
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {projectionData.length - 1}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-calendar"></i>
                      Projection Months
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "risk" && riskMetrics && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-analytics"></i>
                  Risk Analysis
                </h3>
                <div className="risk-visualization">
                  <div className="risk-meter">
                    <div className="meter-label">Risk Level</div>
                    <div className="meter-bar">
                      <div 
                        className="meter-fill"
                        style={{ width: `${Math.min(riskMetrics.riskPercentage, 100)}%` }}
                      ></div>
                    </div>
                    <div className="meter-value">{riskMetrics.riskPercentage}%</div>
                  </div>
                  
                  <div className="position-breakdown">
                    <h4>Position Breakdown</h4>
                    <div className="breakdown-item">
                      <span>Risk per Share:</span>
                      <span>${riskMetrics.riskPerShare.toFixed(2)}</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Position Size:</span>
                      <span>{riskMetrics.positionSize} shares</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Total Investment:</span>
                      <span>${riskMetrics.totalInvestment.toFixed(2)}</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Max Drawdown:</span>
                      <span>{riskMetrics.maxDrawdown}%</span>
                    </div>
                  </div>

                  <div className="margin-info">
                    <h4>Margin Requirements</h4>
                    <div className="breakdown-item">
                      <span>Initial Margin:</span>
                      <span>${inputs.initialMargin}</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Maintenance Margin:</span>
                      <span>${inputs.maintenanceMargin}</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Liquidation Price:</span>
                      <span>${calculateLiquidationPrice()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default ShortLongCalculator;